import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { TrendingUp, TrendingDown, Info, Sliders, Sparkles } from 'lucide-react'
import { RegimeTemplateSelector } from './RegimeTemplateSelector'
import { RegimeVariableOverrides } from './RegimeVariableOverrides'
import type { RegimeTemplate } from '@/hooks/api/useRegimes'
import type { RegimeTemplateListItemDto } from '@/types/api'
import type { BetaMode } from '@/types/api'

// =============================================================================
// Props
// =============================================================================

interface BTCCurveRegimeFormProps {
  btcPricePath: number[]
  betaMode: BetaMode
  selectedTemplateId: string
  templateOverrides: Record<string, number>
  templates: (RegimeTemplate | RegimeTemplateListItemDto)[]
  onBtcPricePathChange: (path: number[]) => void
  onBetaModeChange: (mode: BetaMode) => void
  onTemplateSelect: (templateId: string) => void
  onTemplateOverrideChange: (key: string, value: number | undefined) => void
}

// =============================================================================
// Component
// =============================================================================

export function BTCCurveRegimeForm({
  btcPricePath,
  betaMode,
  selectedTemplateId,
  templateOverrides,
  templates,
  onBtcPricePathChange,
  onBetaModeChange,
  onTemplateSelect,
  onTemplateOverrideChange,
}: BTCCurveRegimeFormProps) {
  const [inputMode, setInputMode] = useState<'simple' | 'advanced'>('simple')
  const [simpleStart, setSimpleStart] = useState<number>(0)
  const [simpleEnd, setSimpleEnd] = useState<number>(-30)

  // Get selected template
  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId) || null

  // Type guard to check if template has variables (is RegimeTemplate, not RegimeTemplateListItemDto)
  const hasVariables = (template: typeof selectedTemplate): template is RegimeTemplate => {
    return template !== null && 'variables' in template
  }

  // Generate linear path from simple inputs
  const generateLinearPath = (start: number, end: number): number[] => {
    const path: number[] = []
    for (let i = 0; i < 24; i++) {
      const pct = start + (end - start) * (i / 23)
      path.push(Number(pct.toFixed(2)))
    }
    return path
  }

  // Handle simple mode changes
  const handleSimpleStartChange = (value: number) => {
    setSimpleStart(value)
    onBtcPricePathChange(generateLinearPath(value, simpleEnd))
  }

  const handleSimpleEndChange = (value: number) => {
    setSimpleEnd(value)
    onBtcPricePathChange(generateLinearPath(simpleStart, value))
  }

  // Handle advanced mode changes
  const handleAdvancedPathChange = (index: number, value: number) => {
    const newPath = [...btcPricePath]
    newPath[index] = value
    onBtcPricePathChange(newPath)
  }

  // Simple sparkline visualization
  const renderSparkline = () => {
    const min = Math.min(...btcPricePath)
    const max = Math.max(...btcPricePath)
    const range = max - min || 1
    const height = 60

    return (
      <div className="h-[60px] w-full bg-muted rounded-lg overflow-hidden relative">
        <svg viewBox={`0 0 ${btcPricePath.length} ${height}`} className="w-full h-full" preserveAspectRatio="none">
          <defs>
            <linearGradient id="sparklineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.5" />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.1" />
            </linearGradient>
          </defs>
          <path
            d={btcPricePath
              .map((value, i) => {
                const x = i
                const y = height - ((value - min) / range) * height * 0.8 - height * 0.1
                return `${i === 0 ? 'M' : 'L'} ${x} ${y}`
              })
              .join(' ')}
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth="0.5"
            vectorEffect="non-scaling-stroke"
          />
          <path
            d={`${btcPricePath
              .map((value, i) => {
                const x = i
                const y = height - ((value - min) / range) * height * 0.8 - height * 0.1
                return `${i === 0 ? 'M' : 'L'} ${x} ${y}`
              })
              .join(' ')} L ${btcPricePath.length - 1} ${height} L 0 ${height} Z`}
            fill="url(#sparklineGradient)"
            stroke="none"
          />
        </svg>
        <div className="absolute top-1 right-2 text-xs text-muted-foreground">
          24-month projection
        </div>
      </div>
    )
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Important Note Alert */}
        <Alert className="bg-amber-50 border-amber-200">
          <Info className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-sm text-amber-800">
            <strong>Important:</strong> Crypto and TradFi are priced independently in this mode. 
            Regime templates contain zero crypto assumptions - they only define TradFi market conditions.
          </AlertDescription>
        </Alert>

        {/* BTC Price Path Input */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                BTC Price Path (24 Months)
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-sm">
                    <p className="text-sm">
                      Define how BTC price changes over 24 months. Other crypto assets are priced 
                      relative to BTC using the selected beta mode. Values are percentage changes from current level.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  variant={inputMode === 'simple' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setInputMode('simple')}
                >
                  <Sparkles className="h-4 w-4 mr-1" />
                  Simple
                </Button>
                <Button
                  variant={inputMode === 'advanced' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setInputMode('advanced')}
                >
                  <Sliders className="h-4 w-4 mr-1" />
                  Advanced
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Sparkline Chart */}
            {renderSparkline()}

            {/* Path Stats */}
            <div className="flex justify-between text-sm">
              <div className="text-muted-foreground">
                Start: <span className={btcPricePath[0] >= 0 ? 'text-green-600' : 'text-red-600'}>
                  {btcPricePath[0] >= 0 ? '+' : ''}{btcPricePath[0]}%
                </span>
              </div>
              <div className="text-muted-foreground">
                End: <span className={btcPricePath[btcPricePath.length - 1] >= 0 ? 'text-green-600' : 'text-red-600'}>
                  {btcPricePath[btcPricePath.length - 1] >= 0 ? '+' : ''}{btcPricePath[btcPricePath.length - 1]}%
                </span>
              </div>
            </div>

            {/* Simple Mode */}
            {inputMode === 'simple' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="btc-start">Start % Change</Label>
                  <div className="relative">
                    <Input
                      id="btc-start"
                      type="number"
                      value={simpleStart}
                      onChange={(e) => handleSimpleStartChange(parseFloat(e.target.value) || 0)}
                      className="text-right"
                    />
                    <span className="absolute right-8 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                      %
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">Month 1</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="btc-end">End % Change</Label>
                  <div className="relative">
                    <Input
                      id="btc-end"
                      type="number"
                      value={simpleEnd}
                      onChange={(e) => handleSimpleEndChange(parseFloat(e.target.value) || 0)}
                      className="text-right"
                    />
                    <span className="absolute right-8 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                      %
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">Month 24</p>
                </div>
              </div>
            )}

            {/* Advanced Mode */}
            {inputMode === 'advanced' && (
              <div className="grid grid-cols-6 md:grid-cols-8 lg:grid-cols-12 gap-2">
                {btcPricePath.map((value, index) => (
                  <div key={index} className="space-y-1">
                    <Label htmlFor={`btc-month-${index}`} className="text-xs">
                      M{index + 1}
                    </Label>
                    <Input
                      id={`btc-month-${index}`}
                      type="number"
                      value={value}
                      onChange={(e) => handleAdvancedPathChange(index, parseFloat(e.target.value) || 0)}
                      className={`text-xs text-right h-8 ${value >= 0 ? 'text-green-600' : 'text-red-600'}`}
                      step={1}
                    />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Beta Mode Selector */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              Beta Mode
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-md">
                  <p className="text-sm mb-2">
                    <strong>Normal:</strong> Beta calculated from full historical period. 
                    Represents typical crypto-BTC correlation in normal market conditions.
                  </p>
                  <p className="text-sm">
                    <strong>Stressed:</strong> Beta calculated from Nov 2021 - Nov 2022 drawdown. 
                    Higher correlation during crisis periods, typically 20%+ higher than normal.
                  </p>
                </TooltipContent>
              </Tooltip>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => onBetaModeChange('NORMAL')}
                className={`p-4 rounded-lg border text-left transition-colors ${
                  betaMode === 'NORMAL'
                    ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                    : 'border-border hover:border-muted-foreground/50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-500" />
                  <span className="font-medium">Normal Beta</span>
                </div>
                <div className="text-sm text-muted-foreground mt-2">
                  Calculated from full historical period (Jan 2021 - present)
                </div>
                <Badge variant="secondary" className="mt-2">
                  Standard correlation
                </Badge>
              </button>

              <button
                type="button"
                onClick={() => onBetaModeChange('STRESSED')}
                className={`p-4 rounded-lg border text-left transition-colors ${
                  betaMode === 'STRESSED'
                    ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                    : 'border-border hover:border-muted-foreground/50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <TrendingDown className="h-5 w-5 text-red-500" />
                  <span className="font-medium">Stressed Beta</span>
                </div>
                <div className="text-sm text-muted-foreground mt-2">
                  Calculated from Nov 2021 - Nov 2022 drawdown period
                </div>
                <Badge variant="destructive" className="mt-2">
                  Crisis correlation (+20%)
                </Badge>
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Regime Template Selection */}
        <RegimeTemplateSelector
          templates={templates}
          selectedId={selectedTemplateId}
          onSelect={onTemplateSelect}
        />

        {/* Variable Overrides */}
        {selectedTemplate && hasVariables(selectedTemplate) && (
          <RegimeVariableOverrides
            template={selectedTemplate}
            overrides={templateOverrides}
            onOverrideChange={onTemplateOverrideChange}
          />
        )}
      </div>
    </TooltipProvider>
  )
}
